import type { Account } from '@services/dto/account';

/**
 * The account a dashboard URL is asking for, when it is one this person actually has.
 *
 * Checked against their own visible accounts, so a URL cannot make an account current that they do
 * not have. This grants nothing - the server authorises every read on its own - it only decides
 * which of their accounts the app considers selected.
 * @param accounts - The person's visible accounts. Undefined while they are still loading.
 * @param pathname - The current path, e.g. `/dashboard/{accountId}/projects/{projectId}`.
 * @returns The account the path names, or undefined when it names none of theirs.
 */
export const accountInPath = (accounts: Account[] | undefined, pathname: string): Account | undefined => {
  const id = /^\/dashboard\/([^/?#]+)/.exec(pathname)?.[1];
  return id ? accounts?.find((a) => a.id === id) : undefined;
};

/**
 * Which of a person's accounts is the current one: the URL's, else the one they last used, else
 * the first they have.
 *
 * The URL goes first, and that is the whole fix. Every dashboard route carries the account it is
 * about - `/dashboard/{accountId}/...` - and nothing read it. The current account came from
 * `localStorage` alone, and the effect that chose it then redirected the browser to match, so
 * opening a link to one of your own accounts while another was the stored one silently replaced the
 * address with the stored account's dashboard root, losing the account, the project and the page in
 * one `navigate`.
 *
 * Not cosmetic. A provider could not reach a provider-account URL at all: every attempt bounced to
 * the client account, which put their own side of a shared project out of reach rather than merely
 * one click away - and out of reach of anyone trying to test it.
 *
 * The two fallbacks behind it are untouched, so an id that is not theirs - a stale link, a
 * hand-edited one - still lands somewhere real instead of nowhere.
 * @param accounts - The person's visible accounts.
 * @param pathname - The current path.
 * @param stored - The id last written to `localStorage`, if any.
 * @returns The account to treat as current, or undefined when they have none.
 */
export const currentAccountFor = (accounts: Account[], pathname: string, stored?: string | null): Account | undefined =>
  accountInPath(accounts, pathname) ||
  accounts.find((x) => x.id === stored) ||
  (accounts.length > 0 ? accounts[0] : undefined);
